/**
 * Diesel Offline Database - IndexedDB wrapper
 * Stores pending cargas, cached vehiculos/conductores for offline use
 */

const DB_NAME = 'diesel_offline';
const DB_VERSION = 1;

const STORES = {
    PENDING_CARGAS: 'pending_cargas',
    CACHED_VEHICULOS: 'cached_vehiculos',
    CACHED_CONDUCTORES: 'cached_conductores',
};

function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORES.PENDING_CARGAS)) {
                db.createObjectStore(STORES.PENDING_CARGAS, { keyPath: 'offline_id' });
            }
            if (!db.objectStoreNames.contains(STORES.CACHED_VEHICULOS)) {
                db.createObjectStore(STORES.CACHED_VEHICULOS, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(STORES.CACHED_CONDUCTORES)) {
                db.createObjectStore(STORES.CACHED_CONDUCTORES, { keyPath: 'id' });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function tx(db, storeName, mode = 'readonly') {
    const transaction = db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
}

// --- Pending Cargas ---

export async function savePendingCarga(carga) {
    const db = await openDB();
    const offlineId = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const record = {
        ...carga,
        offline_id: offlineId,
        created_offline_at: new Date().toISOString(),
        synced: false,
    };
    return new Promise((resolve, reject) => {
        const store = tx(db, STORES.PENDING_CARGAS, 'readwrite');
        const req = store.put(record);
        req.onsuccess = () => resolve(record);
        req.onerror = () => reject(req.error);
    });
}

export async function getPendingCargas() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const store = tx(db, STORES.PENDING_CARGAS);
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result.filter(c => !c.synced));
        req.onerror = () => reject(req.error);
    });
}

export async function markCargaSynced(offlineId) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const store = tx(db, STORES.PENDING_CARGAS, 'readwrite');
        const req = store.delete(offlineId);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

export async function getPendingCount() {
    const pending = await getPendingCargas();
    return pending.length;
}

// --- Cached Vehiculos ---

export async function cacheVehiculos(vehiculos) {
    const db = await openDB();
    const store = tx(db, STORES.CACHED_VEHICULOS, 'readwrite');
    store.clear();
    vehiculos.forEach(v => store.put(v));
}

export async function getCachedVehiculos() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const store = tx(db, STORES.CACHED_VEHICULOS);
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

// --- Cached Conductores ---

export async function cacheConductores(conductores) {
    const db = await openDB();
    const store = tx(db, STORES.CACHED_CONDUCTORES, 'readwrite');
    store.clear();
    conductores.forEach(c => store.put(c));
}

export async function getCachedConductores() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const store = tx(db, STORES.CACHED_CONDUCTORES);
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

// --- Sync Engine ---

export async function syncPendingCargas(apiUrl, token) {
    const pending = await getPendingCargas();
    if (pending.length === 0) return { synced: 0, failed: 0 };

    let synced = 0;
    let failed = 0;

    for (const carga of pending) {
        try {
            const payload = { ...carga };
            delete payload.offline_id;
            delete payload.synced;

            const res = await fetch(`${apiUrl}/api/diesel/cargas`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                await markCargaSynced(carga.offline_id);
                synced++;
            } else {
                failed++;
            }
        } catch {
            failed++;
        }
    }

    return { synced, failed, total: pending.length };
}
